import "../../styles/bootstrap/bootstrap-popover.scss";
import "../../styles/popoverOverride.scss";
import "./inspectorConstraints.scss";

import PropTypes from "prop-types";
import React, { Fragment } from "react";
import { PopoverBody } from "reactstrap";

import NavigationToolbar from "../../components/navigationToolbar";
import { withScreenContext } from "../../components/screen/ScreenContext";
import { ConstraintComparison, ConstraintTarget, ConstraintTypes } from "../../constants/constraints";
import { isScreenModeMobile } from "../../constants/screenModes";
import { Precision, PropTypeShapes } from "../../types";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import preprocessorQuery from "../../utils/preprocessorQuery";
import FormButton from "../form/FormButton";
import Icon from "../interface/Icon";
import { popoverOrPortal } from "../portal/popoverOrPortal";
import InspectorEditDateConstraintView from "./InspectorEditDateConstraintView";

const getTargetLabel = target => (target === ConstraintTarget.Start ? "Start" : "End");
const getComparisonLabel = comparison =>
  comparison === ConstraintComparison.After
    ? "is after"
    : comparison === ConstraintComparison.Before
      ? "is before"
      : "equals";
const getObjectTargetLabel = target => (target === ConstraintTarget.Start ? "start of" : "end of");

const getPartPrecisionLabel = precision =>
  precision === Precision.Week
    ? "weekday"
    : precision === Precision.Day
      ? "day of month"
      : Precision.toString(precision);

const offsetIsPositive = offset =>
  offset.years >= 0 &&
  offset.months >= 0 &&
  offset.weeks >= 0 &&
  offset.days >= 0 &&
  offset.hours >= 0 &&
  offset.minutes >= 0 &&
  offset.seconds >= 0;

const renderRelativeConstraintContent = (constraint, onItemClick, onEditClick) => {
  const header = `${getTargetLabel(constraint.thisTarget)} ${getComparisonLabel(
    constraint.comparison
  )} ${getObjectTargetLabel(constraint.otherTarget)}`;
  const offsetLabel = offsetIsPositive(constraint.offset) ? `+ ${constraint.offset.label}` : constraint.offset.label;
  return (
    <div className="inspectorConstraintContent">
      <span className="inspectorConstraintContentTitle" onClick={onEditClick}>
        {header}
      </span>
      <span
        className={`inspectorConstraintContentValue ${onItemClick ? "clickable" : ""}`}
        onClick={() => onItemClick(constraint.other)}
      >
        {getLabelWithUntitled(constraint.otherLabel)}
      </span>
      <span className="inspectorConstraintContentSubLabel" onClick={onEditClick}>
        {offsetLabel}
      </span>
    </div>
  );
};

const renderDateConstraintContent = (constraint, onEditClick) => {
  const header = `${getTargetLabel(constraint.thisTarget)} ${getComparisonLabel(constraint.comparison)}`;
  return (
    <div className="inspectorConstraintContent" onClick={onEditClick}>
      <span className="inspectorConstraintContentTitle">{header}</span>
      <span className="inspectorConstraintContentValue">{constraint.date.label}</span>
    </div>
  );
};

const renderDatePartConstraintContent = (constraint, onEditClick) => {
  const header = `${getTargetLabel(constraint.thisTarget)} matches ${getPartPrecisionLabel(constraint.partPrecision)}`;
  const content = constraint.partValue;

  return (
    <div className="inspectorConstraintContent" onClick={onEditClick}>
      <span className="inspectorConstraintContentTitle">{header}</span>
      <span className="inspectorConstraintContentValue">{content}</span>
    </div>
  );
};

const renderConstraintContent = (constraint, onItemClick, onEditClick) => {
  if (constraint.constraintType === ConstraintTypes.Relative) {
    return renderRelativeConstraintContent(constraint, onItemClick, onEditClick);
  }
  if (constraint.constraintType === ConstraintTypes.Date) {
    return renderDateConstraintContent(constraint, onEditClick);
  }
  if (constraint.constraintType === ConstraintTypes.DatePart) {
    return renderDatePartConstraintContent(constraint, onEditClick);
  }
};

class InspectorConstraintBox extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      resolvePopoverOpen: false
    };

    this.toggleResolvePopover = this.toggleResolvePopover.bind(this);
    this.renderResolvePopover = this.renderResolvePopover.bind(this);
    this.onChooseResolutionClick = this.onChooseResolutionClick.bind(this);
    this.onRemoveClick = this.onRemoveClick.bind(this);
    this.toggle = this.toggle.bind(this);
    this.closePopover = this.closePopover.bind(this);
    this.onChildPopover = this.onChildPopover.bind(this);
    this._isChildPopoverOn = false;
  }

  toggleResolvePopover() {
    this.setState({
      resolvePopoverOpen: !this.state.resolvePopoverOpen
    });
  }

  onChooseResolutionClick(optionId) {
    this.props.onChooseResolution(optionId, this.props.constraint.other);
    this.toggleResolvePopover();
  }

  onRemoveClick() {
    this.props.onDeleteClick();
    this.toggleResolvePopover();
  }

  toggle() {
    const { popoverOpen } = this.state;
    if (!this._isChildPopoverOn || !popoverOpen) {
      this.setState({
        popoverOpen: !popoverOpen
      });
    }
  }

  closePopover() {
    if (!this._isChildPopoverOn) {
      this.setState({
        popoverOpen: false
      });
    }
  }

  onChildPopover(value) {
    this._isChildPopoverOn = value;
  }

  renderPopover() {
    let placement = "bottom-start";
    let hideArrow = false;
    let alwaysPortal = false;
    //#!if tablet === true
// placement = "right-start";
    //#!endif

    let popoverOptions = {
      className: `inspectorPopover`,
      placement,
      hideArrow,
      isOpen: this.state.popoverOpen,
      target: `constraint-${this.props.constraint.ids.first()}`,
      toggle: null,
      boundariesElement: "viewport"
    };

    const child = (
      <Fragment>
        {this.props.screenContext && isScreenModeMobile(this.props.screenContext.screenMode) ? (
          <NavigationToolbar
            title={this.props.title}
            showBack={true}
            showBackArrow={true}
            onBackClick={this.toggle}
            backText={this.props.backText ? this.props.backText : "Back"}
            flexLeft={1}
            flexCenter={3}
            flexRight={1}
          />
        ) : null}
        <PopoverBody onClick={e => e.stopPropagation()}>
          <InspectorEditDateConstraintView
            calendar={this.props.calendar}
            constraintDependencyOptions={this.props.constraintDependencyOptions}
            onAddDateConstraint={this.props.onAddDateConstraint}
            onAddRelativeConstraint={this.props.onAddRelativeConstraint}
            onAddDatePartConstraint={this.props.onAddDatePartConstraint}
            closePopover={this.closePopover}
            onChildPopover={this.onChildPopover}
            title={this.props.title}
            backText={this.props.backText}
            constraint={this.props.constraint}
          />
        </PopoverBody>
      </Fragment>
    );

    return popoverOrPortal(child, popoverOptions, alwaysPortal);
  }

  renderResolvePopover() {
    const { constraint } = this.props;

    if (!constraint.isViolated) {
      return null;
    }

    let placement = "bottom-start";
    let hideArrow = false;
    let alwaysPortal = false;
    //#!if tablet === true
// placement = "right-start";
    //#!endif

    let popoverOptions = {
      className: `inspectorPopover`,
      placement,
      hideArrow,
      isOpen: this.state.resolvePopoverOpen,
      target: `resolveConstraint-${constraint.ids.size ? constraint.ids.first() : ""}`,
      toggle: null,
      boundariesElement: "viewport"
    };

    const child = (
      <Fragment>
        {this.props.screenContext && isScreenModeMobile(this.props.screenContext.screenMode) ? (
          <NavigationToolbar
            title={this.props.title}
            showBack={true}
            showBackArrow={true}
            onBackClick={this.toggleResolvePopover}
            backText={this.props.backText ? this.props.backText : "Back"}
            flexLeft={1}
            flexCenter={3}
            flexRight={1}
          />
        ) : null}
        <PopoverBody onClick={e => e.stopPropagation()}>
          <div>
            {constraint.resolutionOptions.map(o => {
              return (
                <div key={o.id} className="resolveConstraintOption">
                  <FormButton linkStyle onClick={() => this.onChooseResolutionClick(o.id)} label={o.label} />
                  <div className="inspectorConstraintContentTitle">{o.offset}</div>
                </div>
              );
            })}
            <div className="resolveConstraintOption">
              <FormButton linkStyle onClick={this.onRemoveClick} label="Remove constraint" />
            </div>
          </div>
        </PopoverBody>
      </Fragment>
    );

    return popoverOrPortal(child, popoverOptions, alwaysPortal);
  }

  render() {
    const isIos = preprocessorQuery.isIos();
    const { constraint, onStatusClick, onItemClick, onDeleteClick } = this.props;

    return (
      <div className="inspectorConstraintBox" id={`constraint-${constraint.ids.size ? constraint.ids.first() : ""}`}>
        <Icon
          className="statusIcon"
          icon={constraint.isViolated ? "statusConflict" : "statusValid"}
          color={constraint.isViolated ? "statusError" : "statusValid"}
          onClick={onStatusClick}
        />
        <div style={{ width: "100%" }}>
          {renderConstraintContent(constraint, onItemClick, isIos ? this.toggle : null)}
          {constraint.isViolated ? (
            <FormButton
              linkStyle
              onClick={this.toggleResolvePopover}
              label="Resolve"
              id={`resolveConstraint-${constraint.ids.size ? constraint.ids.first() : ""}`}
            />
          ) : null}
        </div>
        {!isIos ? <Icon className="toolbarEdit" icon="toolbarEdit" color="iconMono" onClick={this.toggle} /> : null}
        <Icon className="deleteIcon" icon="listDelete" color="iconMono" onClick={onDeleteClick} />
        {/* Resolve constraint popover */}
        {this.state.resolvePopoverOpen ? (
          <div className="formListBackdrop" onClick={this.toggleResolvePopover} />
        ) : null}
        {this.state.resolvePopoverOpen ? this.renderResolvePopover() : null}
        {/* Edit constraint popover */}
        {this.state.popoverOpen ? <div className="formListBackdrop" onClick={this.toggle} /> : null}
        {this.state.popoverOpen ? this.renderPopover() : null}
      </div>
    );
  }
}

InspectorConstraintBox.propTypes = {
  constraint: PropTypeShapes.inspectorConstraint,
  onStatusClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  onItemClick: PropTypes.func,
  title: PropTypes.string,
  backText: PropTypes.string,

  calendar: PropTypeShapes.calendar.isRequired,
  constraintDependencyOptions: PropTypeShapes.listOptionList,
  onAddDateConstraint: PropTypes.func,
  onAddRelativeConstraint: PropTypes.func,
  onAddDatePartConstraint: PropTypes.func,
  onChooseResolution: PropTypes.func.isRequired,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

let exportedView = InspectorConstraintBox;
//#!if iOS === true
// exportedView = withScreenContext(InspectorConstraintBox);
//#!endif
export default exportedView;
